package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.CollapsingHeaderLayout
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.core.presentation.common.rememberTextSharer
import com.trevorwiebe.timesheet.loads.presentation.LoadsViewModel
import org.koin.compose.viewmodel.koinViewModel

/**
 * Loads whose effective date is in the past are implicitly completed and surface here, styled like
 * the Mileage Log: a collapsing filter header, a pinned column-title header, and a table of rows.
 * The Employee/Start/End filters default to a rolling last-two-months window; tapping a card opens
 * the shared Add/Edit Load dialog. Reuses [LoadsViewModel] (its own instance) so the edit dialog and
 * its save/delete machinery come for free.
 */
@Composable
fun CompletedLoadsScreen(
    onBack: () -> Unit,
    viewModel: LoadsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val bottomPadding = botPadding()
    val shareText = rememberTextSharer()

    Scaffold(
        topBar = {
            TopBar(
                title = "Completed Loads",
                backIcon = { Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back") },
                onBack = onBack,
                actions = {
                    if (state.isCompletedFiltered) {
                        TextButton(onClick = { viewModel.onEvent(LoadsEvent.OnClearFilters) }) {
                            Text("Clear Filters", color = MaterialTheme.colorScheme.tertiary)
                        }
                    }
                    val hasLoads = state.completedLoads.isNotEmpty()
                    IconButton(
                        onClick = { shareText(state.completedLoadsShareText) },
                        enabled = hasLoads,
                    ) {
                        Icon(
                            Icons.Default.Share,
                            contentDescription = "Share",
                            tint = MaterialTheme.colorScheme.tertiary.copy(
                                alpha = if (hasLoads) 1f else 0.38f,
                            ),
                        )
                    }
                },
            )
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
            CollapsingHeaderLayout(
                header = {
                    Column {
                        CompletedLoadsFilterBar(state = state, onEvent = viewModel::onEvent)
                        HorizontalDivider()
                    }
                },
                pinnedHeader = {
                    Column {
                        CompletedLoadsTableHeader()
                        HorizontalDivider()
                    }
                },
            ) { contentPadding ->
                val loads = state.completedLoads
                when {
                    state.isLoading -> Box(modifier = Modifier.fillMaxSize()) {
                        CircularProgressIndicator(
                            modifier = Modifier.align(Alignment.Center),
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }

                    loads.isEmpty() -> Box(modifier = Modifier.fillMaxSize()) {
                        Text(
                            text = "No completed loads match the current filters.",
                            modifier = Modifier.align(Alignment.Center).padding(16.dp),
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center,
                        )
                    }

                    else -> LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = contentPadding,
                    ) {
                        items(loads, key = { it.id }) { load ->
                            LoadCard(
                                load = load,
                                locations = state.locations,
                                onClick = { viewModel.onEvent(LoadsEvent.OnEditLoad(load)) },
                                trailingDate = Util.toNumericMonthDay(load.scheduledDate),
                                driverName = state.completedLoadDriverName(load),
                            )
                        }
                        item { Spacer(Modifier.height(bottomPadding)) }
                    }
                }
            }
        }
    }

    if (state.showAddDialog) {
        AddLoadDialog(state = state, onEvent = viewModel::onEvent)
    }
}

@Composable
private fun CompletedLoadsTableHeader() {
    Surface(color = MaterialTheme.colorScheme.background) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = LoadCardStartPadding + StatusBadgeWidth, end = 8.dp, top = 10.dp, bottom = 10.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = "Pickup",
                modifier = Modifier.weight(1f),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = "Drop",
                modifier = Modifier.weight(1f),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = "Date",
                modifier = Modifier.width(CompletedTrailingWidth),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                textAlign = TextAlign.End,
            )
        }
    }
}
